import { useEffect } from "react"
import { CircleMarker, MapContainer, Popup, TileLayer, useMap } from "react-leaflet"
import type { SmartOLTMapOdb, SmartOLTMapOnu } from "../../../services/smartolt.service.ts"
import { SMARTOLT_PATHS } from "../smartoltNav.ts"
import { formatSignal, markerColor, ODB_COLOR } from "./smartoltMapUtils.ts"
import "leaflet/dist/leaflet.css"

const DEFAULT_CENTER: [number, number] = [31.5204, 74.3587]

function FitMarkers({ points }: { points: [number, number][] }) {
  const map = useMap()
  const serialized = points.map((p) => p.join(",")).join("|")
  useEffect(() => {
    const next = serialized
      ? serialized.split("|").map((pair) => pair.split(",").map(Number) as [number, number])
      : []
    if (!next.length) {
      map.setView(DEFAULT_CENTER, 11)
      return
    }
    if (next.length === 1) {
      map.setView(next[0], 15)
      return
    }
    map.fitBounds(next, { padding: [36, 36], maxZoom: 16 })
  }, [map, serialized])
  return null
}

export function SmartOLTMapCanvas({
  onus,
  odbs,
  showCoverage,
  selectedId,
  onSelectOnu,
  onSelectOdb,
}: {
  onus: SmartOLTMapOnu[]
  odbs: SmartOLTMapOdb[]
  showCoverage: boolean
  selectedId?: string | null
  onSelectOnu: (row: SmartOLTMapOnu) => void
  onSelectOdb: (row: SmartOLTMapOdb) => void
}) {
  const points: [number, number][] = [
    ...onus.map((row) => [row.latitude, row.longitude] as [number, number]),
    ...odbs.map((row) => [row.latitude, row.longitude] as [number, number]),
  ]

  return (
    <MapContainer center={DEFAULT_CENTER} zoom={12} className="h-full w-full" scrollWheelZoom>
      <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      <FitMarkers points={points} />
      {odbs.map((row) => (
        <CircleMarker
          key={`odb-${row.id}`}
          center={[row.latitude, row.longitude]}
          radius={selectedId === row.id ? 10 : 7}
          pathOptions={{ color: "#0f172a", weight: selectedId === row.id ? 2 : 1, fillColor: ODB_COLOR, fillOpacity: 0.85 }}
          eventHandlers={{ click: () => onSelectOdb(row) }}
        >
          <Popup>
            <div className="text-xs">
              <div className="font-semibold">{row.name}</div>
              <div>{row.zone_name || "ODB"}</div>
            </div>
          </Popup>
        </CircleMarker>
      ))}
      {onus.map((row) => (
        <CircleMarker
          key={`onu-${row.id}`}
          center={[row.latitude, row.longitude]}
          radius={selectedId === row.id ? 9 : 6}
          pathOptions={{
            color: "#0f172a",
            weight: selectedId === row.id ? 2 : 1,
            fillColor: markerColor(row, showCoverage),
            fillOpacity: 0.85,
          }}
          eventHandlers={{ click: () => onSelectOnu(row) }}
        >
          <Popup>
            <div className="space-y-1 text-xs">
              <div className="font-semibold">{row.name}</div>
              <div>{row.status_kind} · {formatSignal(row.signal_rx)}</div>
              <a href={SMARTOLT_PATHS.onuDetail(row.onu_external_id)}>ONU details</a>
            </div>
          </Popup>
        </CircleMarker>
      ))}
    </MapContainer>
  )
}
